/**
 * <coming-soon-notify> — quiet "notify me when it drops" waitlist form.
 *
 * On submit it makes two client-side Klaviyo API calls (no libraries):
 *   1. subscribes the email to the drop list (company_id = public key)
 *   2. fires a per-product "Subscribed to Drop" event so each waitlist keeps its
 *      own history (profile properties would overwrite across multiple drops)
 *
 * Config comes from data attributes set by snippets/coming-soon-notify.liquid.
 * A filled honeypot silently no-ops as a success to deter bots.
 */
const KLAVIYO_REVISION = '2025-04-15';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class ComingSoonNotify extends HTMLElement {
  connectedCallback() {
    this.publicKey = this.dataset.publicKey;
    this.listId = this.dataset.listId;
    if (!this.publicKey || !this.listId) return;

    this.form = this.querySelector('.js-coming-soon-form');
    this.emailInput = this.querySelector('.js-coming-soon-email');
    this.honeypot = this.querySelector('.js-coming-soon-hp');
    this.submitBtn = this.querySelector('.js-coming-soon-submit');
    this.message = this.querySelector('.js-coming-soon-message');
    if (!this.form) return;

    if (this.bound) return;
    this.bound = true;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    if (this.pending) return;

    if (this.honeypot && this.honeypot.value) {
      this.showMessage('success');
      return;
    }

    const email = (this.emailInput.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.emailInput.focus();
      this.showMessage('error');
      return;
    }

    this.setPending(true);
    this.subscribe(email)
      .then(() => {
        // Success reflects the subscription only — the event is fire-and-forget
        // so a failed/blocked events call can't mask a completed subscribe.
        this.trackEvent(email).catch(() => {});
        this.showMessage('success');
        this.form.reset();
      })
      .catch(() => this.showMessage('error'))
      .finally(() => this.setPending(false));
  }

  subscribe(email) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Coming Soon Waitlist',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.listId } },
        },
      },
    };
    return this.post('client/subscriptions', body);
  }

  trackEvent(email) {
    const properties = {
      drop_product_handle: this.dataset.productHandle,
      drop_product_title: this.dataset.productTitle,
      drop_product_url: this.dataset.productUrl,
    };
    if (this.dataset.dropAt) properties.drop_at = this.dataset.dropAt;

    const body = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: { data: { type: 'metric', attributes: { name: 'Subscribed to Drop' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
      },
    };
    return this.post('client/events', body);
  }

  post(path, body) {
    return fetch(`https://a.klaviyo.com/${path}/?company_id=${encodeURIComponent(this.publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`Klaviyo ${path} responded ${response.status}`);
      return response;
    });
  }

  setPending(pending) {
    this.pending = pending;
    if (!this.submitBtn) return;
    this.submitBtn.disabled = pending;
    const label = pending ? this.submitBtn.dataset.labelPending : this.submitBtn.dataset.labelDefault;
    if (label) this.submitBtn.textContent = label;
  }

  showMessage(state) {
    if (!this.message) return;
    const text = state === 'success' ? this.message.dataset.labelSuccess : this.message.dataset.labelError;
    // The status node is always in the DOM (empty) so aria-live announces the
    // text swap; we never toggle hidden in the same tick as setting the text.
    this.message.textContent = text;
    this.message.classList.toggle('coming-soon-notify__message--success', state === 'success');
    this.message.classList.toggle('coming-soon-notify__message--error', state === 'error');
    if (state === 'success') {
      if (this.form) this.form.querySelector('.coming-soon-notify__row').hidden = true;
      this.message.focus();
    }
  }
}

if (!customElements.get('coming-soon-notify')) {
  customElements.define('coming-soon-notify', ComingSoonNotify);
}
